import { at, compose, get, indexOf, map, sortBy, update, __ } from 'lodash/fp';
import { createSelector } from 'reselect';
import { array, option } from 'fp-ts';
import { constUndefined, flow } from 'fp-ts/function';
import { composeProps, createAsyncMetaSelectors, createPropsExtender } from '@mews/framework';
import { getOrZero, ifElse } from '@mews/fp';
import { ResourceCategoryType } from '@mews-commander/legacy-data-structures';
import { zipArgs } from '@mews-commander/utils';
import { getSortedResourceCategoryIds } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import {
    getAgeCategoriesByIds,
    getSortedActiveAgeCategoryIdsByServiceId,
} from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { getRateName } from '@mews-commander/legacy-entities/src/ducks/rates';
import { getServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks/services';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { transformOccupancy, transformPricing } from '../utils';
import { moduleSelector } from '../module';

export const parentDataSelector = createSelector(moduleSelector, get('data'));
const dataMetaSelector = createSelector(parentDataSelector, get('meta'));

export const [isRateManagementDataFetching, hasRateManagementDataFetchingFailed] =
    createAsyncMetaSelectors(dataMetaSelector);

export const getSelectedProductId = createSelector(parentDataSelector, get('productId'));
export const getSelectedRateId = createSelector(parentDataSelector, get('rateId'));
export const getSelectedServiceId = createSelector(parentDataSelector, get('serviceId'));
export const getSelectedServiceTimeUnit = composeProps(
    getServiceTimeUnit,
    createPropsExtender('id', getSelectedServiceId)
);

const selectedServiceIdPropsExtender = createPropsExtender('serviceId', getSelectedServiceId);

const getSelectedSortedResourceCategoryIds = composeProps(getSortedResourceCategoryIds, selectedServiceIdPropsExtender);
const getSelectedSortedAgeCategoryIds = composeProps(
    getSortedActiveAgeCategoryIdsByServiceId,
    selectedServiceIdPropsExtender
);

export const getSelectedSortedNonDefaultAgeCategories = flow(
    composeProps(getAgeCategoriesByIds, createPropsExtender('ids', getSelectedSortedAgeCategoryIds)),
    array.filter(({ isDefault }) => !isDefault)
);

const getOccupancyData = createSelector(parentDataSelector, get('occupancy'));

const getTotalOccupancies = createSelector(getOccupancyData, get('totalOccupancies'));
const getResourceCategoryTypeOccupancies = createSelector(getOccupancyData, get('resourceCategoryTypeOccupancies'));

const getSortedResourceCategoryTypeOccupancies = (state: $TSFixMe, props: $TSFixMe) => {
    // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
    const resourceCategoryTypeOccupancies = getResourceCategoryTypeOccupancies(state, props);
    const { appLocalizationComponent } = getContainer()();
    return sortBy(
        compose(
            (enumValue) => appLocalizationComponent.localizeEnumify(enumValue),
            (resourceCategoryType: $TSFixMe) => ResourceCategoryType.getByName(resourceCategoryType),
            get('resourceCategoryType')
        ),
        resourceCategoryTypeOccupancies
    );
};

export const getResourceCategoryTypeOccupanciesWithSortedResourceCategories = createSelector(
    getSelectedSortedResourceCategoryIds,
    getSortedResourceCategoryTypeOccupancies,
    (sortedIds, resourceCategoryTypeOccupancies) =>
        map(
            update('categoryOccupancies', sortBy(compose(indexOf(__, sortedIds), get('resourceCategoryId')))),
            resourceCategoryTypeOccupancies
        )
);

const getMappedOccupancyData = createSelector(
    // @ts-expect-error
    getResourceCategoryTypeOccupanciesWithSortedResourceCategories,
    transformOccupancy
);
export const getOccupancyAsideData = createSelector(getMappedOccupancyData, get('aside'));
export const getOccupancyBodyData = createSelector(getMappedOccupancyData, get('body'));

export const getOccupancyRowsData = createSelector(
    getTotalOccupancies,
    getOccupancyBodyData,
    zipArgs(['totalOccupancies', 'occupancyBodyData'])
);

const getPricingData = createSelector(parentDataSelector, get('pricing'));

export const getPricingAttributeType = createSelector(getPricingData, get('attributeType'));
export const getBasePrice = createSelector(getPricingData, get('basePrice'));
export const getAttributePrices = createSelector(getPricingData, get('attributePrices'));
export const getPricingAttributeIds = createSelector(getAttributePrices, Object.keys);

export const getBasePricePricing = createSelector(getBasePrice, get('prices'));
export const getBasePriceAmount = createSelector(getBasePrice, get('value'));

export const getBasePriceCurrency = createSelector(getBasePriceAmount, get('currency'));
export const getBasePriceTaxValues = createSelector(getBasePriceAmount, get('taxValues'));
export const getBasePriceValue = createSelector(getBasePriceAmount, getOrZero('value'));
export const getBasePriceNetValue = createSelector(getBasePriceAmount, getOrZero('netValue'));
export const getBasePriceFinalValue = ifElse(
    isCurrentEnterpriseInMultiTaxEnvironment,
    getBasePriceNetValue,
    getBasePriceValue
);
export const getBasePriceFinalAmount = createSelector(
    getBasePriceCurrency,
    getBasePriceFinalValue,
    getBasePriceTaxValues,
    zipArgs(['currency', 'value', 'taxValues'])
);

export const getCapacityOffsetAdjustments = createSelector(getPricingData, get('capacityOffsetAdjustments'));
const getRateAdjustment = flow(getPricingData, ({ rateAdjustment }) => option.fromNullable(rateAdjustment));

export const getTaxExcemptionReason = createSelector(getBasePrice, get('taxExemptionReasonCode'));

export const getLegalReference = createSelector(getBasePrice, get('taxExemptionLegalReferenceValue'));

export const hasRateAdjustment = flow(getRateAdjustment, option.isSome);

export const getRateAdjustmentType = flow(
    getRateAdjustment,
    option.map(({ discriminator }) => discriminator),
    option.getOrElseW(constUndefined)
);
export const getRateAdjustmentValue = flow(
    getRateAdjustment,
    option.map(({ value }) => value),
    option.getOrElseW(constUndefined)
);

export const getBaseRateId = flow(getRateAdjustmentValue, ({ baseRateId }) => baseRateId);
export const getBaseRateName = composeProps(getRateName, createPropsExtender('rateId', getBaseRateId));

// @ts-expect-error $TSFixMeError: No overload matches this call.
const getResourceCategoryPricingData = createSelector(getSelectedSortedResourceCategoryIds, getAttributePrices, at);

// @ts-expect-error $TSFixMeError: No overload matches this call.
const getAgeCategoryPricingData = createSelector(getSelectedSortedAgeCategoryIds, getAttributePrices, at);

const createMappedPricingDataSelector = (pricingDataSelector: $TSFixMe) =>
    createSelector(getBasePricePricing, pricingDataSelector, (basePricePricing, pricingData) => {
        // @ts-expect-error $TSFixMeError: Argument of type 'unknown' is not assignable to parameter of type 'AttributePrice[]'.
        const { aside, body } = transformPricing(pricingData);
        return {
            basePricePricing,
            pricingAsideData: aside,
            pricingBodyData: body,
        };
    });

export const getMappedAgeCategoryPricingData = createMappedPricingDataSelector(getAgeCategoryPricingData);
export const getMappedResourceCategoryPricingData = createMappedPricingDataSelector(getResourceCategoryPricingData);
